import { useState } from 'react';
import { toast } from 'sonner';
import { ExternalLink, ImagePlus, Images, Link2, Star, X } from 'lucide-react';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';
import {
  MAX_REFERENCES_PER_SHOT,
  toHttpsUrl,
  urlsFromTransfer,
} from '../../lib/treatmentReferences';
import ReferenceFrame from './ReferenceFrame';
import FrameLightbox from './FrameLightbox';

/**
 * The frames that show a shot's look. Drag them in from Frameset or any page,
 * or paste a link. Only the link is kept - see lib/treatmentReferences.js.
 */
export default function ShotReferences({ shot, number, handlers }) {
  const [dragging, setDragging] = useState(false);
  const [link, setLink] = useState('');
  const [editingSource, setEditingSource] = useState(null); // reference id whose source link is open
  const [viewing, setViewing] = useState(null); // index of the frame shown large
  const refs = shot.references || [];
  const full = refs.length >= MAX_REFERENCES_PER_SHOT;

  const add = (urls, hadFiles = false) => {
    if (!urls.length) {
      toast.error(hadFiles
        ? "Frames are linked, not uploaded. Drag the image from a web page, or paste its link."
        : 'No https image link found in that');
      return false;
    }
    const known = new Set(refs.map((r) => r.image_url));
    const fresh = urls.filter((u) => !known.has(u));
    const room = MAX_REFERENCES_PER_SHOT - refs.length;
    handlers.addReferences(shot.id, fresh);
    if (!fresh.length) toast('Already on this shot');
    else if (fresh.length > room) toast.error(`Added ${room}; a shot holds ${MAX_REFERENCES_PER_SHOT} frames`);
    return true;
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    add(urlsFromTransfer(e.dataTransfer), e.dataTransfer.files?.length > 0);
  };

  const addLink = () => {
    const url = toHttpsUrl(link);
    if (!url) {
      toast.error('Paste an image link starting with https://');
      return;
    }
    if (add([url])) setLink('');
  };

  return (
    <section
      aria-labelledby={`refs-${shot.id}`}
      className={cn('space-y-3 rounded-tile -m-2 p-2 transition-colors', dragging && 'bg-mist ring-2 ring-line-strong')}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
        if (!dragging) setDragging(true);
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setDragging(false);
      }}
      onDrop={onDrop}
      data-testid="shot-references"
    >
      <h3 id={`refs-${shot.id}`} className="text-base flex items-center gap-2">
        <Images className="w-4 h-4" /> Reference frames
        {refs.length > 0 && <span className="text-muted-foreground tabular-nums">{refs.length}</span>}
      </h3>

      <div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(180px,1fr))]">
        {refs.map((ref, i) => (
          <figure key={ref.id} className="m-0 flex flex-col gap-1.5" data-testid="reference-frame">
            <div className="relative aspect-[2.39/1] rounded-cards overflow-hidden bg-mist group">
              <button
                type="button"
                onClick={() => setViewing(i)}
                aria-label={`View frame ${i + 1} larger`}
                className="block w-full h-full cursor-zoom-in focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus"
              >
                <ReferenceFrame url={ref.image_url} alt={ref.note || `Reference ${i + 1}`} />
              </button>
              {i === 0 && (
                <span className="absolute top-1.5 left-1.5 text-[11px] font-medium px-2 py-0.5 rounded-pill bg-inverse/80 text-inverse-foreground">Cover</span>
              )}
              {/* Actions appear on hover or focus; always on touch screens, which can't hover. */}
              <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity">
                {i > 0 && (
                  <button
                    type="button"
                    aria-label="Make this the cover frame"
                    title="Make cover"
                    onClick={() => handlers.makeCoverReference(shot.id, ref.id)}
                    className="h-7 w-7 rounded-pill bg-inverse/70 text-inverse-foreground flex items-center justify-center hover:bg-inverse/90"
                  >
                    <Star className="w-3.5 h-3.5" />
                  </button>
                )}
                <button
                  type="button"
                  aria-label={ref.source_url ? 'Edit the source link' : 'Add a source link'}
                  aria-expanded={editingSource === ref.id}
                  title="Source link"
                  onClick={() => setEditingSource((id) => (id === ref.id ? null : ref.id))}
                  className={cn('h-7 w-7 rounded-pill flex items-center justify-center', ref.source_url ? 'bg-parchment text-foreground hover:bg-parchment/90' : 'bg-inverse/70 text-inverse-foreground hover:bg-inverse/90')}
                >
                  <Link2 className="w-3.5 h-3.5" />
                </button>
                <a
                  href={ref.source_url || ref.image_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={ref.source_url ? 'Open the source page' : 'Open the full frame'}
                  title={ref.source_url ? 'Open source' : 'Open full frame'}
                  className="h-7 w-7 rounded-pill bg-inverse/70 text-inverse-foreground flex items-center justify-center hover:bg-inverse/90"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
                <button
                  type="button"
                  aria-label="Remove this frame"
                  title="Remove"
                  onClick={() => handlers.removeReference(shot.id, ref.id)}
                  className="h-7 w-7 rounded-pill bg-inverse/70 text-inverse-foreground flex items-center justify-center hover:bg-brick hover:text-on-danger"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <Input
              aria-label={`Note for frame ${i + 1}`}
              placeholder="What to take from it"
              value={ref.note || ''}
              onChange={(e) => handlers.updateReference(shot.id, ref.id, { note: e.target.value })}
              className="h-8 text-xs italic"
            />
            {editingSource === ref.id && (
              <Input
                autoFocus
                aria-label={`Source link for frame ${i + 1}`}
                placeholder="https://… the page it came from"
                value={ref.source_url || ''}
                onChange={(e) => handlers.updateReference(shot.id, ref.id, { source_url: e.target.value || null })}
                onKeyDown={(e) => e.key === 'Enter' && setEditingSource(null)}
                className="h-8 text-xs"
              />
            )}
          </figure>
        ))}

        {!full && (
          <div className="flex flex-col gap-1.5">
            <div
              className={cn(
                'aspect-[2.39/1] rounded-cards border-2 border-dashed flex flex-col items-center justify-center gap-1 text-xs text-center px-2',
                dragging ? 'border-foreground text-foreground' : 'border-line-strong text-muted-foreground',
              )}
            >
              <ImagePlus className="w-5 h-5" aria-hidden="true" />
              {dragging ? 'Drop to add' : 'Drag frames here'}
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                addLink();
              }}
            >
              <Input
                aria-label="Image link"
                placeholder="or paste a link"
                value={link}
                onChange={(e) => setLink(e.target.value)}
                onPaste={(e) => {
                  // Several links pasted at once go straight in.
                  const urls = urlsFromTransfer(e.clipboardData);
                  if (urls.length > 1) {
                    e.preventDefault();
                    add(urls);
                  }
                }}
                className="h-8 text-xs"
                data-testid="reference-link-input"
              />
            </form>
          </div>
        )}
      </div>
      <FrameLightbox frames={refs} index={viewing} onIndexChange={setViewing} title={shot.title || 'Untitled'} number={number} />
    </section>
  );
}
